import { animate, domAnimation, LazyMotion, m, useInView } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import useIsLoggedIn from "../../hooks/use-IsLoggedIn";
import Header from "../Profile/components/Header";
import Footer from "../StaticPages/Footer";

// ─── Data ────────────────────────────────────────────────

const SCORE = 87;
const RADIUS = 76;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const categories = [
	{ name: "Education", percentage: 95, match: true, delay: 0 },
	{ name: "Experience", percentage: 78, match: true, delay: 0.1 },
	{ name: "Skills", percentage: 90, match: true, delay: 0.2 },
	{ name: "Certifications", percentage: 40, match: false, delay: 0.3 },
] as const;

const steps = [
	{
		number: "01",
		title: "Choose a Job",
		description: "Pick from our job listings, or paste any job description you've found.",
	},
	{
		number: "02",
		title: "Add Your Resume",
		description: "Upload a file, paste text, or use your Dproz Smart Profile.",
	},
	{
		number: "03",
		title: "Get Your Score",
		description: "Instant match analysis with strengths, gaps, and clear next steps.",
	},
];

const benefits = [
	"Know your strengths before the interview",
	"Spot gaps early and close them fast",
	"Get clear guidance: apply now or prepare first",
	"Stop wasting time on jobs that aren't the right fit",
];

const stagger = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: { staggerChildren: 0.12, delayChildren: 0.1 },
	},
};

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const fadeUp = {
	hidden: { opacity: 0, y: 24 },
	show: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.6, ease: EASE },
	},
};

// ─── Sub-components ──────────────────────────────────────

function AnimatedNumber({ value }: { value: number }) {
	const ref = useRef<HTMLSpanElement>(null);
	const isInView = useInView(ref, { once: true });

	useEffect(() => {
		if (!isInView || !ref.current) return;
		const node = ref.current;
		const controls = animate(0, value, {
			duration: 2,
			ease: EASE,
			onUpdate(v) {
				node.textContent = `${Math.round(v)}%`;
			},
		});
		return () => controls.stop();
	}, [isInView, value]);

	return (
		<span
			ref={ref}
			className="font-plus-jakarta text-5xl font-extrabold text-blue-900"
			style={{ fontVariantNumeric: "tabular-nums" }}
		>
			0%
		</span>
	);
}

function ScoreVisualization() {
	return (
		<div className="bg-white rounded-2xl border border-gray-200/60 shadow-xl shadow-blue-950/6 p-6 sm:p-8 w-full max-w-sm mx-auto lg:ml-auto lg:mr-0">
			{/* Score Ring */}
			<div className="flex justify-center mb-7">
				<div className="relative w-40 h-40 sm:w-44 sm:h-44">
					<svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
						<circle cx="100" cy="100" r={RADIUS} fill="none" strokeWidth="10" className="stroke-blue-100" />
						<m.circle
							cx="100"
							cy="100"
							r={RADIUS}
							fill="none"
							strokeWidth="10"
							className="stroke-blue-600"
							strokeLinecap="round"
							strokeDasharray={CIRCUMFERENCE}
							initial={{ strokeDashoffset: CIRCUMFERENCE }}
							whileInView={{ strokeDashoffset: CIRCUMFERENCE * (1 - SCORE / 100) }}
							viewport={{ once: true }}
							transition={{ duration: 2, ease: EASE }}
						/>
					</svg>
					<div className="absolute inset-0 flex flex-col items-center justify-center">
						<AnimatedNumber value={SCORE} />
						<span className="font-inter text-sm text-blue-600/70 font-medium mt-0.5">Match</span>
					</div>
				</div>
			</div>

			{/* Category Bars */}
			<div className="space-y-3">
				{categories.map((cat) => (
					<div key={cat.name} className="flex items-center gap-3">
						<span className="font-inter text-xs font-medium text-gray-500 w-24 shrink-0">{cat.name}</span>
						<div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
							<m.div
								className={`h-full rounded-full ${cat.match ? "bg-emerald-500" : "bg-amber-400"}`}
								initial={{ width: 0 }}
								whileInView={{ width: `${cat.percentage}%` }}
								viewport={{ once: true }}
								transition={{
									duration: 1.2,
									delay: 0.8 + cat.delay,
									ease: EASE,
								}}
							/>
						</div>
						<span
							className={`font-inter text-[11px] font-semibold w-10 text-right ${
								cat.match ? "text-emerald-600" : "text-amber-600"
							}`}
						>
							{cat.match ? "Match" : "Gap"}
						</span>
					</div>
				))}
			</div>

			{/* Verdict */}
			<div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-2">
				<div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center">
					<Check className="w-3 h-3 text-emerald-600" strokeWidth={3} />
				</div>
				<span className="font-inter text-sm font-semibold text-gray-800">Ready to Apply</span>
			</div>
		</div>
	);
}

// ─── Main Component ──────────────────────────────────────

function FitCheckLandingPage() {
	const isLoggedIn = useIsLoggedIn();

	return (
		<LazyMotion features={domAnimation} strict>
			<Header isLoggedIn={isLoggedIn} onChangeJobStatus={() => {}} />

			<main>
				{/* ── Hero ─────────────────────────────────── */}
				<section className="pt-12 sm:pt-20 pb-16 sm:pb-24 px-4 sm:px-6">
					<div className="max-w-6xl mx-auto">
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
							{/* Text */}
							<m.div variants={stagger} initial="hidden" animate="show">
								<m.h1
									className="font-plus-jakarta text-[clamp(2.25rem,5.5vw,4rem)] font-extrabold text-gray-900 leading-[1.08] tracking-tight mb-6"
									variants={fadeUp}
								>
									Know Exactly
									<br />
									Where You Stand.
								</m.h1>
								<m.p className="font-inter text-lg text-gray-600 leading-relaxed max-w-lg mb-8" variants={fadeUp}>
									See how your resume matches any job — your strengths, your gaps, and exactly what to fix before you
									apply.
								</m.p>
								<m.div className="flex flex-wrap items-center gap-4" variants={fadeUp}>
									<Link
										to="/fitchecks"
										className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors font-inter"
									>
										Try FitCheckMe
										<ArrowRight className="w-4 h-4" />
									</Link>
									<a
										href="#how-it-works"
										className="font-inter text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
									>
										See how it works
									</a>
								</m.div>
							</m.div>

							{/* Score Visualization */}
							<m.div
								initial={{ opacity: 0, y: 32 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
							>
								<ScoreVisualization />
							</m.div>
						</div>
					</div>
				</section>

				{/* ── How It Works ─────────────────────────── */}
				<section id="how-it-works" className="py-20 sm:py-28 bg-gray-50/70 px-4 sm:px-6">
					<div className="max-w-4xl mx-auto">
						<m.div
							className="text-center mb-16"
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: "-80px" }}
							transition={{ duration: 0.6, ease: EASE }}
						>
							<h2 className="font-plus-jakarta text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-gray-900 tracking-tight mb-4">
								Three Steps to Clarity
							</h2>
							<p className="font-inter text-base text-gray-500 max-w-lg mx-auto">
								From choosing a job to getting your match score — under a minute.
							</p>
						</m.div>

						<div className="relative">
							{/* Connecting line — desktop */}
							<div className="hidden md:block absolute top-6 left-[16.67%] right-[16.67%] h-px bg-blue-200" />
							{/* Connecting line — mobile */}
							<div className="md:hidden absolute top-0 bottom-0 left-6 w-px bg-blue-200" />

							<div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
								{steps.map((step, i) => (
									<m.div
										key={step.number}
										className="relative flex md:flex-col items-start md:items-center gap-5 md:gap-0"
										initial={{ opacity: 0, y: 20 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true, margin: "-40px" }}
										transition={{
											duration: 0.5,
											delay: i * 0.12,
											ease: EASE,
										}}
									>
										<div className="w-12 h-12 rounded-full bg-blue-800 text-white flex items-center justify-center font-plus-jakarta font-bold text-sm shrink-0 relative z-10">
											{step.number}
										</div>
										<div className="md:mt-6 md:text-center">
											<h3 className="font-plus-jakarta font-bold text-lg text-gray-900 mb-1">{step.title}</h3>
											<p className="font-inter text-sm text-gray-500 leading-relaxed max-w-xs">{step.description}</p>
										</div>
									</m.div>
								))}
							</div>
						</div>
					</div>
				</section>

				{/* ── Benefits ─────────────────────────────── */}
				<section className="py-20 sm:py-28 px-4 sm:px-6">
					<div className="max-w-6xl mx-auto">
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
							{/* Image */}
							<m.div
								initial={{ opacity: 0, x: -24 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{ once: true, margin: "-80px" }}
								transition={{ duration: 0.6, ease: EASE }}
								className="order-2 lg:order-1"
							>
								<div className="bg-gray-50 rounded-2xl p-6 sm:p-10">
									<img
										src="/images/home/fitcheck-mockup.png"
										alt="FitCheck detailed report showing strengths and gaps"
										className="w-full h-auto rounded-lg"
										onError={(e) => {
											const parent = e.currentTarget.closest("[data-img-wrapper]");
											if (parent instanceof HTMLElement) parent.style.display = "none";
										}}
									/>
								</div>
							</m.div>

							{/* Content */}
							<m.div
								className="order-1 lg:order-2"
								initial={{ opacity: 0, y: 24 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true, margin: "-80px" }}
								transition={{ duration: 0.6, ease: EASE }}
							>
								<h2 className="font-plus-jakarta text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-gray-900 tracking-tight mb-4">
									Apply Smarter, <span className="text-blue-700">Not Harder.</span>
								</h2>
								<p className="font-inter text-base text-gray-500 leading-relaxed mb-8 max-w-md">
									FitCheck breaks down your match across education, experience, skills, and certifications — so you know
									exactly where you stand.
								</p>
								<ul className="space-y-4 mb-8">
									{benefits.map((benefit) => (
										<li key={benefit} className="flex items-start gap-3">
											<div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mt-0.5 shrink-0">
												<Check className="w-3 h-3 text-blue-700" strokeWidth={3} />
											</div>
											<span className="font-inter text-base text-gray-700">{benefit}</span>
										</li>
									))}
								</ul>
								<Link
									to="/fitchecks"
									className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors font-inter"
								>
									Get Started
									<ArrowRight className="w-4 h-4" />
								</Link>
							</m.div>
						</div>
					</div>
				</section>

				{/* ── CTA ──────────────────────────────────── */}
				<section className="px-4 sm:px-6 pb-16 sm:pb-24">
					<m.div
						className="max-w-4xl mx-auto bg-blue-800 rounded-2xl py-16 sm:py-20 px-6 sm:px-12 text-center relative overflow-hidden"
						initial={{ opacity: 0, y: 24 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, margin: "-64px" }}
						transition={{ duration: 0.6, ease: EASE }}
					>
						{/* Subtle dot pattern */}
						<div
							className="absolute inset-0 opacity-[0.04]"
							style={{
								backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
								backgroundSize: "28px 28px",
							}}
						/>
						<div className="relative z-10">
							<h2 className="font-plus-jakarta text-[clamp(1.75rem,4.5vw,2.75rem)] font-bold text-white tracking-tight leading-tight mb-4">
								Ready to See Where You Stand?
							</h2>
							<p className="font-inter text-base sm:text-lg text-blue-200/60 leading-relaxed mb-8 max-w-md mx-auto">
								Your next application could be your best one.
							</p>
							<Link
								to="/fitchecks"
								className="inline-flex items-center gap-2 bg-white text-blue-800 font-semibold py-3 px-7 rounded-lg hover:bg-blue-50 transition-colors font-inter"
							>
								Try FitCheckMe — It's Free
								<ArrowRight className="w-4 h-4" />
							</Link>
						</div>
					</m.div>
				</section>
			</main>

			<Footer />
		</LazyMotion>
	);
}

export default FitCheckLandingPage;
